/**
 * Student Portal — shared UI primitives.
 * All components are visually driven by /student/styles.css which is
 * scoped under `.student-portal`. Nothing here mutates global state.
 */
import React from 'react';
import { Search, X, ChevronRight, AlertCircle, Check } from 'lucide-react';

export const Card = ({ title, subtitle, actions, children, testId }) => (
  <section className="sp-card" data-testid={testId}>
    {(title || actions) && (
      <header className="sp-card-header">
        <div>
          {title && <h3 className="sp-card-title">{title}</h3>}
          {subtitle && <p className="sp-card-subtitle">{subtitle}</p>}
        </div>
        {actions && <div>{actions}</div>}
      </header>
    )}
    {children}
  </section>
);

export const EmptyState = ({ icon: Icon = Search, title, message, action, testId }) => (
  <div className="sp-empty" data-testid={testId}>
    <div className="sp-empty-icon"><Icon size={18} /></div>
    <p className="sp-empty-title">{title}</p>
    {message && <p className="sp-empty-lede">{message}</p>}
    {action && <div style={{ marginTop: 16 }}>{action}</div>}
  </div>
);

/**
 * Status pill. `variant` = neutral | info | warn | ok | bad
 */
export const StatusPill = ({ variant = 'neutral', children, ...rest }) => (
  <span className={`sp-status sp-status-${variant}`} {...rest}>{children}</span>
);

/**
 * Map a request/return status to a pill variant + label.
 */
export const orderStatusPill = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'pending')   return { variant: 'warn', label: 'Awaiting Approval' };
  if (s === 'approved')  return { variant: 'info', label: 'Approved' };
  if (s === 'issued')    return { variant: 'ok',   label: 'Issued' };
  if (s === 'completed') return { variant: 'ok',   label: 'Completed' };
  if (s === 'returned')  return { variant: 'ok',   label: 'Returned' };
  if (s === 'rejected')  return { variant: 'bad',  label: 'Rejected' };
  if (s === 'cancelled') return { variant: 'neutral', label: 'Cancelled' };
  return { variant: 'neutral', label: status || 'Unknown' };
};

export const returnStatusPill = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'pending')         return { variant: 'warn', label: 'Awaiting Clerk' };
  if (s === 'clerk_verified')  return { variant: 'info', label: 'Clerk Verified' };
  if (s === 'approved')        return { variant: 'ok',   label: 'Return Completed' };
  if (s === 'rejected')        return { variant: 'bad',  label: 'Rejected' };
  return { variant: 'neutral', label: status || 'Unknown' };
};

/**
 * A neutral component "monogram" — first character of the brand (or component
 * name if brand is unknown) on a soft hairline card. Replaces the need for
 * stock imagery until real component images exist.
 */
export const ComponentMonogram = ({ brand, name, size = 56 }) => {
  const letter = (brand || name || '·').trim().charAt(0).toUpperCase() || '·';
  return (
    <div
      className="sp-monogram"
      style={{ width: size, height: size }}
      aria-hidden="true"
      title={brand || name || ''}
    >
      {letter}
    </div>
  );
};

/**
 * A neutral, hairline-only SVG "silhouette" of a generic implantology
 * component. Deliberately abstract — it suggests a precision medical
 * component without pretending to be a photograph. The tile can be swapped
 * to a real image later without touching the surrounding card layout.
 *
 * The <div className="sp-tile-visual"> wrapper handles background /
 * gradient; this component only renders the icon and an optional brand
 * badge in the corner.
 */
export const ComponentSilhouette = ({ brand, name, size = 46, badge = true }) => {
  const letter = (brand || name || '·').trim().charAt(0).toUpperCase() || '·';
  return (
    <div className="sp-tile-visual" aria-hidden="true">
      {/* Abstract abutment/screw silhouette — hairline strokes only */}
      <svg width={size} height={size + 6} viewBox="0 0 46 52" fill="none">
        <path d="M14 3h18l-2 5H16z"    stroke="currentColor" strokeWidth="1" strokeLinejoin="round" />
        <path d="M15 8h16v6H15z"       stroke="currentColor" strokeWidth="1" />
        <path d="M17 14h12l-1 4H18z"   stroke="currentColor" strokeWidth="1" strokeLinejoin="round" />
        <path
          d="M18 18h10v2h-1v3h1v2h-1v3h1v2h-1v3h1v2h-1v3h1v2h-2l-2 3h-4l-2-3h-2v-2h1v-3h-1v-2h1v-3h-1v-2h1v-3h-1v-2h1v-3h-1z"
          stroke="currentColor" strokeWidth="1" strokeLinejoin="miter"
        />
        <line x1="14" y1="3" x2="32" y2="3" stroke="currentColor" strokeWidth="0.8" strokeDasharray="1 1" opacity="0.5" />
      </svg>
      {badge && <span className="sp-tile-visual-badge" title={brand || ''}>{letter}</span>}
    </div>
  );
};

/**
 * A minimal debounced search input.
 */
export const SearchInput = ({ value, onChange, placeholder, testId, autoFocus }) => (
  <div className="sp-search">
    <Search size={16} className="sp-search-icon" />
    <input
      className="sp-input"
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoFocus={autoFocus}
      data-testid={testId}
    />
  </div>
);

/**
 * Base modal. Body renders inside .sp-modal.
 */
export const Modal = ({ open, onClose, title, subtitle, wide, children, footer, testId }) => {
  if (!open) return null;
  return (
    <div className="sp-modal-scrim" onClick={onClose} role="dialog" aria-modal="true">
      <div
        className={`sp-modal${wide ? ' sp-modal-wide' : ''}`}
        onClick={(e) => e.stopPropagation()}
        data-testid={testId}
      >
        <header className="sp-modal-header">
          <div>
            <h3 className="sp-card-title">{title}</h3>
            {subtitle && <p className="sp-card-subtitle">{subtitle}</p>}
          </div>
          <button
            type="button"
            className="sp-topbar-menu-btn"
            onClick={onClose}
            aria-label="Close"
            data-testid="modal-close-btn"
          ><X size={16} /></button>
        </header>
        <div className="sp-modal-body">{children}</div>
        {footer && <div className="sp-modal-footer">{footer}</div>}
      </div>
    </div>
  );
};

/**
 * Toast — inline element controlled by state. Rendered at the shell level.
 */
export const Toast = ({ toast, onDismiss }) => {
  if (!toast) return null;
  const cls = toast.variant === 'ok' ? 'sp-toast sp-toast-ok'
            : toast.variant === 'bad' ? 'sp-toast sp-toast-bad'
            : 'sp-toast';
  const Icon = toast.variant === 'bad' ? AlertCircle : Check;
  return (
    <div className={cls} role="status" data-testid="portal-toast">
      <Icon size={18} style={{ flexShrink: 0, marginTop: 1 }} />
      <div style={{ flex: 1 }}>{toast.message}</div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{ background: 'transparent', border: 0, color: 'inherit', cursor: 'pointer', opacity: 0.7 }}
      ><X size={16} /></button>
    </div>
  );
};

export const Chevron = () => <ChevronRight size={16} strokeWidth={1.6} />;
